// Reunderwriting Module (Tier 3) PR 4 — left-rail filters for the
// reunderwriting Failure Tracking page. Presentational; URL-syncing
// happens in the parent.

import { Card, FormField, Input } from "@/design-system/components/ui";
import type { CriteriaLane, FailureStatus } from "@/types/reunderwriting";

interface Props {
  status: FailureStatus | null;
  lane: CriteriaLane | null;
  from: string;
  to: string;
  onChange: (next: Partial<Record<string, string | null>>) => void;
}

const STATUS_OPTIONS: { value: FailureStatus; label: string }[] = [
  { value: "Open", label: "Open" },
  { value: "Reviewed", label: "Reviewed" },
  { value: "Resolved", label: "Resolved" },
];

const LANE_OPTIONS: { value: CriteriaLane; label: string }[] = [
  { value: "A1", label: "A1" },
  { value: "A2", label: "A2" },
  { value: "A3", label: "A3" },
  { value: "A4", label: "A4" },
];

export function ReunderwritingFailureFilterSidebar({
  status,
  lane,
  from,
  to,
  onChange,
}: Props) {
  return (
    <Card className="p-space-4 flex flex-col gap-space-4" aria-label="Filters">
      <div role="group" aria-labelledby="ru-fail-status">
        <h2
          id="ru-fail-status"
          className="font-display text-label uppercase tracking-label text-onyx mb-space-2"
        >
          Status
        </h2>
        <div className="flex flex-col gap-space-1">
          {STATUS_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="inline-flex items-center gap-space-2 text-body text-onyx cursor-pointer"
            >
              <input
                type="radio"
                name="ru-fail-status-radio"
                checked={status === opt.value}
                onChange={() => onChange({ status: opt.value })}
              />
              <span>{opt.label}</span>
            </label>
          ))}
          <button
            type="button"
            className="text-caption text-gold underline self-start"
            onClick={() => onChange({ status: null })}
          >
            Any status
          </button>
        </div>
      </div>

      <div role="group" aria-labelledby="ru-fail-lane">
        <h2
          id="ru-fail-lane"
          className="font-display text-label uppercase tracking-label text-onyx mb-space-2"
        >
          Lane
        </h2>
        <div className="flex flex-col gap-space-1">
          {LANE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="inline-flex items-center gap-space-2 text-body text-onyx cursor-pointer"
            >
              <input
                type="radio"
                name="ru-fail-lane-radio"
                checked={lane === opt.value}
                onChange={() => onChange({ lane: opt.value })}
              />
              <span>{opt.label}</span>
            </label>
          ))}
          <button
            type="button"
            className="text-caption text-gold underline self-start"
            onClick={() => onChange({ lane: null })}
          >
            Any lane
          </button>
        </div>
      </div>

      <div>
        <h2 className="font-display text-label uppercase tracking-label text-onyx mb-space-2">
          Date range
        </h2>
        <div className="flex flex-col gap-space-2">
          <FormField htmlFor="ru-fail-from" label="From">
            <Input
              id="ru-fail-from"
              type="date"
              value={from}
              onChange={(e) => onChange({ from: e.target.value || null })}
            />
          </FormField>
          <FormField htmlFor="ru-fail-to" label="To">
            <Input
              id="ru-fail-to"
              type="date"
              value={to}
              onChange={(e) => onChange({ to: e.target.value || null })}
            />
          </FormField>
        </div>
      </div>
    </Card>
  );
}
